import React, { useState, useEffect, useMemo } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { CreditCardApplicationContext } from '../../CreditCardApplicationContext';
import { PageNotFound } from '../404';
import { Footer } from 'src/commons/components/Footer';
import { Nav } from 'src/commons/components/Nav/Nav';
import { Page } from 'src/commons/components/Page';
import {
  ApplicationType,
  ApplicationStatus,
  ENTITY_NOT_FOUND_MESSAGE,
  PageNames,
  SET_PAYMENT_DATE_OPTIMIZELY_KEY,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import { getCreditCardApplication } from 'src/commons/data/queries';
import { ifFeatureFlagOn } from 'src/commons/utilities/feature-flags';
import {
  heapAddUserProperties,
  heapIdentify,
  logError,
  nrAddPageProperty,
  nrIdentify,
} from 'src/commons/utilities/logging';
import { prefixRoute } from 'src/commons/utilities/routes';
import { useExitPrompt } from 'src/commons/utilities/useExitPrompt';

import { Agreements } from './Agreements';
import { AppResume, getResumeRouteFromAppStatus } from './AppResume';
import { AutoPay } from './AutoPay';
import { ConnectToBank } from './ConnectToBank';
import { Decline } from './Decline';
import { ExportWaitingRoom } from './ExportWaitingRoom';
import { FinishUp } from './FinishUp';
import { Offer } from './Offer';
import { OfferWaitingRoom } from './OfferWaitingRoom';
import { PaymentDate } from './PaymentDate';
import { VerificationEmail } from './VerificationEmail';
import { VerificationWaitingRoom } from './VerificationWaitingRoom';

export const Application = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Have a useRef() for each footnote and superscript
  const footnoteRef = [React.useRef(null), React.useRef(null)];

  // State ---------------------------------------------------------------------------------------
  const [application, setApplication] = useState<ApplicationType>({
    applicationId: '',
    hasConsentedToTcpa: false,
    externalUserId: '',
    applicant: {
      applicantId: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      dateOfBirth: '',
      ssn: '',
      address: {
        street1: '',
        city: '',
        state: '',
        zip: '',
      },
      income: {
        incomeSourceTypes: [],
        annualIncome: '',
        housingPayment: '',
      },
    },
    offerCheck: null,
    selectedBankAccount: {},
    plaidLinkToken: '',
    applicationAttribution: null,
  });

  const { applicationId } = useParams();
  const [fetchingApplication, setFetchingApplication] = useState(true);
  const [pollingApplication, setPollingApplication] = useState(false);
  const [showApplicationFetchError, setShowApplicationFetchError] =
    useState(false);
  const [showPollingError, setShowPollingError] = useState(false);

  // keeps track of whether the sign out button has been clicked
  const [signOutClicked, setSignOutClicked] = useState(false);

  // When the app status is VERIFICATION_IN_PROGRESS, only show the waiting room once
  const [showVerificationWaitingRoom, setShowVerificationWaitingRoom] =
    useState(false);

  // when the beforeunload prompt is triggered
  const onExitPromptTrigger = () => {
    // flip the sign out flag back to false
    setSignOutClicked(false);
  };

  // prompt the user to confirm before leaving (on the offer page)
  const shouldShowExitPrompt = useMemo(
    () => pathname.endsWith('offer'),
    [pathname]
  );
  useExitPrompt(
    'Are you sure you want to leave?',
    shouldShowExitPrompt,
    signOutClicked,
    onExitPromptTrigger
  );

  useEffect(() => {
    if (
      application &&
      application.applicationId &&
      application.externalUserId &&
      !pollingApplication
    ) {
      const {
        applicationId: appId,
        externalUserId,
        applicationAttribution,
      } = application;
      heapIdentify(externalUserId);
      heapAddUserProperties({ creditCardApplicationId: `${appId}` });
      nrIdentify(externalUserId);
      nrAddPageProperty('externalApplicationId', appId);
      nrAddPageProperty(
        'applicationAttributionCrc',
        applicationAttribution?.campaignRefCode
      );
    }
  }, [application]);

  const castConditionalOnCashflowVerification = (
    creditCardApp: ApplicationType
  ) => {
    // Shallow copy
    const ccApp = creditCardApp;
    if (
      creditCardApp.offerCheck &&
      creditCardApp.offerCheck.offer &&
      creditCardApp.offerCheck.offer.conditionalOnCashflowVerification === null
    ) {
      // Null conditionalOnCashflowVerification is assumed to be true
      ccApp.offerCheck.offer.conditionalOnCashflowVerification = true;
    }
    return ccApp;
  };

  const fetchCreditCardApplication = async () => {
    // indicate we're fetching the app
    setFetchingApplication(true);
    try {
      // try to fetch the app
      const response = await getCreditCardApplication(applicationId);
      const { data, errors } = response;
      if (data && data.getCreditCardApplication) {
        // set application response to CreditCardApplicationContext
        setApplication(
          castConditionalOnCashflowVerification(data.getCreditCardApplication)
        );
      }
      if (errors) {
        // if they tried to fetch an application they don't own
        if (errors[0] && errors[0].code === ENTITY_NOT_FOUND_MESSAGE) {
          // just drop them off at the apply page
          // this will resume their proper app
          navigate(prefixRoute(''), { replace: true });
        } else {
          setShowApplicationFetchError(true);
        }
      }
      setFetchingApplication(false);
    } catch (err: any) {
      // as long as this wasn't a 401
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        // indicate that the fetch failed
        setShowApplicationFetchError(true);
        setFetchingApplication(false);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'Application',
          function: 'fetchCreditCardApplication',
        });
      }
    }
  };

  // called when pages are loaded
  // fetches a fresh application and redirects to the correct page if needed
  const fetchAndResumeCreditCardApplication = async (pageName: PageNames) => {
    try {
      const response = await getCreditCardApplication(applicationId);
      const { data, errors } = response;
      if (data && data.getCreditCardApplication) {
        // set application response to CreditCardApplicationContext
        setApplication(
          castConditionalOnCashflowVerification(data.getCreditCardApplication)
        );
        // redirect if necessary
        const { applicationStatus, autopayPreferences } =
          data.getCreditCardApplication;
        const { monthlyPaymentDay } = autopayPreferences || {};
        const showSetPaymentDate = ifFeatureFlagOn(
          SET_PAYMENT_DATE_OPTIMIZELY_KEY
        );
        switch (pageName) {
          case PageNames.OFFER:
            if (
              applicationStatus !== ApplicationStatus.PENDING_OFFER_ACCEPT &&
              applicationStatus !==
                ApplicationStatus.PENDING_UNCONDITIONAL_OFFER_ACCEPT
            ) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication)
              );
            }
            break;
          case PageNames.OFFER_WAITING_ROOM:
            if (
              applicationStatus !== ApplicationStatus.OFFER_IN_PROGRESS &&
              applicationStatus !== ApplicationStatus.OFFER_ERROR &&
              applicationStatus !==
                ApplicationStatus.UNCONDITIONAL_VERIFICATION_IN_PROGRESS &&
              applicationStatus !==
                ApplicationStatus.UNCONDITIONAL_VERIFICATION_ERROR &&
              applicationStatus !==
                ApplicationStatus.PENDING_CONDITIONAL_DEVICE_VALIDATION &&
              applicationStatus !==
                ApplicationStatus.PENDING_UNCONDITIONAL_DEVICE_VALIDATION
            ) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication)
              );
            }
            break;
          case PageNames.BANK_CONNECT:
            if (
              applicationStatus !==
                ApplicationStatus.PENDING_BANK_CONNECT_AUTHORIZATION &&
              applicationStatus !== ApplicationStatus.PENDING_BANK_CONNECT
            ) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication)
              );
            }
            break;
          case PageNames.VERIFICATION_WAITING_ROOM:
            if (applicationStatus !== ApplicationStatus.VERIFICATION_ERROR) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication),
                { replace: true }
              );
            }
            break;
          case PageNames.VERIFICATION_EMAIL:
            if (
              applicationStatus !== ApplicationStatus.VERIFICATION_IN_PROGRESS
            ) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication)
              );
            }
            break;
          case PageNames.AUTO_PAY:
            if (
              (applicationStatus !== ApplicationStatus.PENDING_SUBMISSION &&
                applicationStatus !==
                  ApplicationStatus.PENDING_UNCONDITIONAL_SUBMISSION) ||
              autopayPreferences
            ) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication)
              );
            }
            break;
          case PageNames.PAYMENT_DATE:
            if (
              (applicationStatus !== ApplicationStatus.PENDING_SUBMISSION &&
                applicationStatus !==
                  ApplicationStatus.PENDING_UNCONDITIONAL_SUBMISSION) ||
              monthlyPaymentDay ||
              !showSetPaymentDate
            ) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication)
              );
            }
            break;
          case PageNames.EXPORT_WAITING_ROOM:
            if (
              applicationStatus !== ApplicationStatus.EXPORT_IN_PROGRESS &&
              applicationStatus !== ApplicationStatus.EXPORT_ERROR
            ) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication)
              );
            }
            break;
          case PageNames.FINISH:
            if (applicationStatus !== ApplicationStatus.APPLICATION_COMPLETED) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication)
              );
            }
            break;
          case PageNames.DECLINE:
            if (
              applicationStatus !== ApplicationStatus.REJECTION_IN_PROGRESS &&
              applicationStatus !== ApplicationStatus.REJECTION_ERROR &&
              applicationStatus !== ApplicationStatus.APPLICATION_REJECTED
            ) {
              navigate(
                getResumeRouteFromAppStatus(data.getCreditCardApplication)
              );
            }
            break;
          default:
            break;
        }
        setFetchingApplication(false);
      }
      if (errors) {
        setFetchingApplication(false);
        // if they tried to fetch an application they don't own
        if (errors[0] && errors[0].code === ENTITY_NOT_FOUND_MESSAGE) {
          // just drop them off at the apply page
          // this will resume their proper app
          navigate(prefixRoute(''), { replace: true });
        } else {
          setShowApplicationFetchError(true);
        }
      }
    } catch (err: any) {
      // setShowError to display 'Something went wrong'
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setShowApplicationFetchError(true);
        setFetchingApplication(false);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'Application',
          function: 'fetchAndResumeCreditCardApplication',
        });
      }
    }
  };

  const pollCreditCardApplication = async () => {
    // indicate we're polling the app
    setPollingApplication(true);
    setShowPollingError(false);
    try {
      // try to fetch the app
      const response = await getCreditCardApplication(applicationId);
      const { data, errors } = response;
      if (data && data.getCreditCardApplication) {
        // set application response to CreditCardApplicationContext
        setApplication(
          castConditionalOnCashflowVerification(data.getCreditCardApplication)
        );
      }
      if (errors) {
        // if they tried to fetch an application they don't own
        if (errors[0] && errors[0].code === ENTITY_NOT_FOUND_MESSAGE) {
          // just drop them off at the apply page
          // this will resume their proper app
          navigate(prefixRoute(''), { replace: true });
        } else {
          setShowPollingError(true);
        }
      }
      setPollingApplication(false);
    } catch (err: any) {
      // as long as this wasn't a 401
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        // indicate that the fetch failed
        setShowPollingError(true);
        setPollingApplication(false);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'Application',
          function: 'pollCreditCardApplication',
        });
      }
    }
  };

  // Show if we're not fetching, not errored, have an offer, and on the right page
  const shouldShowPostOfferFooter = () =>
    !showApplicationFetchError &&
    !fetchingApplication &&
    !showPollingError &&
    !pollingApplication &&
    application.offerCheck &&
    application.offerCheck.offer &&
    !!application.offerCheck.offer.creditLimit &&
    !(
      pathname.includes('decline') ||
      pathname.includes('agreements') ||
      pathname.includes('offer-waiting')
    );

  // Callback for when superscripts on pages are activated
  // Allows footnotes to be ADA friendly
  // Note: future linkable superscripts should follow this standard, especially id and href names
  const handleSuperscriptActivation = (e: any, footnote: number) => {
    e.preventDefault();
    // ensure footnotes are loaded
    if (footnoteRef[footnote - 1].current) {
      // smooth scroll to footnote section
      footnoteRef[footnote - 1].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      const footnoteChildElementId = `footnote-${footnote}`;
      // grab the footnote element's anchor child via id
      // specifically cast as HTMLAnchorElement so that href can be defined
      const fn = document.getElementById(
        footnoteRef[footnote - 1].current.children[footnoteChildElementId].id
      ) as HTMLAnchorElement;
      if (e.target.parentElement && e.target.parentElement.id) {
        // dynamically set element href so that it can scroll back to the correct original content
        // set to the origin superscript's parent element id
        fn.href = `#${e.target.parentElement.id}`;
      }
    }
  };

  // Handle superscript click
  const onSuperscriptClick = (e: any, footnote: number) => {
    handleSuperscriptActivation(e, footnote);
  };

  // Handle superscript enter
  const onSuperscriptEnter = (e: any, footnote: number) => {
    if (e.key === 'Enter') {
      handleSuperscriptActivation(e, footnote);
    }
  };

  const onSignOutClick = () => {
    setSignOutClicked(true);
  };

  const creditCardProviderValue = useMemo(
    () => ({
      creditCardApplication: application,
      fetchCreditCardApplication,
      fetchAndResumeCreditCardApplication,
      fetchingApplication,
      showApplicationFetchError,
      showVerificationWaitingRoom,
      setShowVerificationWaitingRoom,
      pollCreditCardApplication,
      pollingApplication,
      showPollingError,
      onSuperscriptClick,
      onSuperscriptEnter,
    }),
    [
      application,
      fetchCreditCardApplication,
      fetchAndResumeCreditCardApplication,
      fetchingApplication,
      showApplicationFetchError,
      showVerificationWaitingRoom,
      setShowVerificationWaitingRoom,
      pollCreditCardApplication,
      pollingApplication,
      showPollingError,
      onSuperscriptClick,
      onSuperscriptEnter,
    ]
  );

  return (
    <CreditCardApplicationContext.Provider value={creditCardProviderValue}>
      <Nav onSignOutClick={shouldShowExitPrompt ? onSignOutClick : undefined} />
      <Page>
        <Routes>
          <Route path="/agreements" element={<Agreements />} />
          <Route path="/offer-waiting-room" element={<OfferWaitingRoom />} />
          <Route path="/offer" element={<Offer />} />
          <Route path="/connect-to-bank" element={<ConnectToBank />} />
          <Route
            path="/verification-waiting-room"
            element={<VerificationWaitingRoom />}
          />
          <Route path="/verification-email" element={<VerificationEmail />} />
          {/* AutoPay path needs a trailing '*' since it has a separate <Routes /> within it */}
          <Route path="/auto-pay/*" element={<AutoPay />} />
          <Route path="/payment-date" element={<PaymentDate />} />
          <Route path="/finish-up" element={<FinishUp />} />
          <Route path="/export-waiting-room" element={<ExportWaitingRoom />} />
          <Route path="/decline/:declineCode?" element={<Decline />} />
          <Route path="/" element={<AppResume />} />
          <Route path="/*" element={<PageNotFound />} />
        </Routes>
        <Footer
          postOffer={shouldShowPostOfferFooter()}
          footnoteRef={footnoteRef}
          conditionalOnCashflowVerification={
            application.offerCheck &&
            application.offerCheck.offer &&
            application.offerCheck.offer.conditionalOnCashflowVerification
          }
        />
      </Page>
    </CreditCardApplicationContext.Provider>
  );
};
